import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  getProfilePicture,
  getDiceAvatarUrl,
  handleProfilePictureError,
} from '../utils/utils';

const ProfilePicture = ({
  user,
  alt = 'Profile',
  style = {},
  className = '',
  onError = null,
  context = '',
  ...props
}) => {
  // Memoize the image URLs to prevent recalculation on every render
  const primaryImageSrc = useMemo(() => {
    if (!user) return null;
    return getProfilePicture(user);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email, user?.id, user?.profile_picture, user?.avatar]);

  const fallbackImageSrc = useMemo(() => {
    if (!user) return 'https://api.dicebear.com/9.x/glass/svg?seed=default';
    return getDiceAvatarUrl(user);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email, user?.id]);

  const [currentSrc, setCurrentSrc] = useState(() => {
    if (!user) return 'https://api.dicebear.com/9.x/glass/svg?seed=default';
    return getDiceAvatarUrl(user);
  });
  const [isLoading, setIsLoading] = useState(true);
  const hasErrored = useRef(false);

  useEffect(() => {
    // Reset error state when user changes
    hasErrored.current = false;
    setIsLoading(true);

    // If no primary image source, use fallback immediately
    if (!primaryImageSrc) {
      setCurrentSrc(fallbackImageSrc);
      setIsLoading(false);
      return;
    }

    // If primary image is same as fallback, just use it
    if (primaryImageSrc === fallbackImageSrc) {
      setCurrentSrc(fallbackImageSrc);
      setIsLoading(false);
      return;
    }

    // Preload the primary image
    const img = new Image();

    img.onload = () => {
      if (!hasErrored.current) {
        setCurrentSrc(primaryImageSrc);
        setIsLoading(false);
      }
    };

    img.onerror = () => {
      if (!hasErrored.current) {
        hasErrored.current = true;
        handleProfilePictureError(
          { target: img },
          user,
          context || 'ProfilePicture component'
        );
        setCurrentSrc(fallbackImageSrc);
        setIsLoading(false);

        if (onError) {
          onError({ target: img });
        }
      }
    };

    // Start loading the primary image
    img.src = primaryImageSrc;

    // Cleanup function
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryImageSrc, fallbackImageSrc, context]);

  const handleDirectImageError = (e) => {
    // This should rarely be called since we preload, but just in case
    if (!hasErrored.current) {
      hasErrored.current = true;
      handleProfilePictureError(e, user, context || 'ProfilePicture component');
      setCurrentSrc(fallbackImageSrc);

      if (onError) {
        onError(e);
      }
    }

    // Prevent further error events
    e.preventDefault();
    return false;
  };

  return (
    <img
      src={currentSrc}
      alt={alt}
      style={{
        ...style,
        opacity: isLoading ? 0.7 : 1,
        transition: 'opacity 0.3s ease',
      }}
      className={className}
      onError={handleDirectImageError}
      {...props}
    />
  );
};

export default ProfilePicture;
